/**
 * templateVersioning — client-side draft & version log for inspection templates.
 *
 * The production Pit Link backend does not have native `version` / `is_draft` /
 * `published_by` columns on `/api/inspection-templates`, so the Owner Config
 * Centre uses a hybrid strategy:
 *
 *   1. **Live templates** live only on the server. When the Owner clicks
 *      "Publish" we PATCH/POST the sanitised body PLUS a suffix appended to
 *      the description of the form " [vN · Owner Name · YYYY-MM-DD]" so the
 *      version stamp survives on any client that reads the template — even
 *      the mobile app, which has no versioning code.
 *
 *   2. **Drafts** live only in `localStorage[sl_template_drafts_v1]`. They
 *      never touch the server until the Owner clicks Publish. This is the
 *      "safe" path — no half-finished template can accidentally reach the
 *      field.
 *
 *   3. **Version history** lives in `localStorage[sl_template_versions_v1]`.
 *      Every successful Publish appends an entry `{ template_id, version,
 *      published_by, published_at, name, section_count, item_count }` so
 *      the Owner can see the audit trail even if the server has no idea.
 *
 * Historical completed reports (inspections/PPFs/breakdowns) are already
 * version-locked by construction: the backend snapshots `sections` onto the
 * inspection record at creation time, so future template edits never
 * retroactively change a signed inspection.
 */

const DRAFTS_KEY = "sl_template_drafts_v1";
const VERSIONS_KEY = "sl_template_versions_v1";

function safeGet(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function safeSet(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; }
}

// ---- Drafts ---------------------------------------------------------------
export function listDrafts() {
  return safeGet(DRAFTS_KEY) || [];
}
export function getDraft(draftId) {
  return listDrafts().find((d) => d.draft_id === draftId) || null;
}
export function saveDraft(draft) {
  const drafts = listDrafts();
  const id = draft.draft_id || draft.id || `draft_${Date.now()}`;
  const withMeta = {
    ...draft,
    draft_id: id,
    saved_at: new Date().toISOString(),
    is_draft: true,
  };
  const i = drafts.findIndex((d) => d.draft_id === id);
  if (i >= 0) drafts[i] = withMeta; else drafts.push(withMeta);
  safeSet(DRAFTS_KEY, drafts);
  return withMeta;
}
export function deleteDraft(draftId) {
  const drafts = listDrafts().filter((d) => d.draft_id !== draftId);
  safeSet(DRAFTS_KEY, drafts);
}

// ---- Version log ----------------------------------------------------------
export function listVersions(templateId) {
  const all = safeGet(VERSIONS_KEY) || [];
  return templateId ? all.filter((v) => v.template_id === templateId) : all;
}
export function nextVersion(templateId) {
  const rows = listVersions(templateId);
  return rows.length ? Math.max(...rows.map((r) => Number(r.version) || 0)) + 1 : 1;
}
export function recordPublish({ templateId, version, publishedBy, name, sections }) {
  const all = safeGet(VERSIONS_KEY) || [];
  const secCount = (sections || []).length;
  const itemCount = (sections || []).reduce((a, s) => a + (s.items || []).length, 0);
  const entry = {
    template_id: templateId,
    version,
    published_by: publishedBy || "Unknown",
    published_at: new Date().toISOString(),
    name,
    section_count: secCount,
    item_count: itemCount,
  };
  all.push(entry);
  safeSet(VERSIONS_KEY, all);
  return entry;
}

// ---- Description version stamp -------------------------------------------
// Any current template description may already have a `[vN · Owner · date]`
// suffix from a previous publish. We strip that first so we don't stack.
const STAMP_RE = /\s*\[v\d+\s*·[^\]]*\]\s*$/i;
export function stripVersionStamp(desc) {
  return String(desc || "").replace(STAMP_RE, "").trim();
}
export function formatVersionStamp({ version, publishedBy, publishedAt }) {
  const date = new Date(publishedAt || Date.now()).toISOString().slice(0, 10);
  const who = String(publishedBy || "Owner").trim().slice(0, 40);
  return `[v${version} · ${who} · ${date}]`;
}
export function attachVersionStamp(desc, meta) {
  const base = stripVersionStamp(desc);
  const stamp = formatVersionStamp(meta);
  return base ? `${base} ${stamp}` : stamp;
}
// Detect an inline version stamp when reading a template so we can surface
// it as a pill in the UI even for records the portal didn't publish itself.
export function parseInlineVersion(desc) {
  const m = STAMP_RE.exec(String(desc || ""));
  if (!m) return null;
  const inner = m[0].trim().replace(/^\[|\]$/g, "").trim();
  const parts = inner.split("·").map((s) => s.trim());
  if (!parts.length) return null;
  const vMatch = /v(\d+)/i.exec(parts[0] || "");
  return {
    version: vMatch ? Number(vMatch[1]) : null,
    published_by: parts[1] || null,
    published_at: parts[2] || null,
  };
}
